/**
 * Image utilities for validation, optimization, and processing
 */

export const MEDIA_CONFIG = {
  VIDEO_MAX_FILE_SIZE: 30 * 1024 * 1024, // 30MB
  VIDEO_FORMATS: ['video/mp4'] as readonly string[],
} as const;

export const IMAGE_CONFIG = {
  MAX_FILE_SIZE: 10 * 1024 * 1024, // 10MB
  MAX_IMAGES: 10,
  ACCEPTED_FORMATS: ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif', 'video/mp4'],
  MAX_WIDTH: 1920,
  MAX_HEIGHT: 1920,
  THUMBNAIL_SIZE: 200,
  COMPRESSION_QUALITY: 0.85,
} as const;

export interface ImageValidationError {
  type: 'format' | 'size' | 'dimensions' | 'count';
  message: string;
  file?: File;
}

export interface ProcessedImage {
  file: File;
  preview: string;
  thumbnail?: string;
}

/**
 * Validate image file
 */
/**
 * Check if a file is a video based on MIME type
 */
export function isVideoFile(file: File): boolean {
  return MEDIA_CONFIG.VIDEO_FORMATS.includes(file.type);
}

export function validateImageFile(file: File): ImageValidationError | null {
  // Check format
  if (!(IMAGE_CONFIG.ACCEPTED_FORMATS as readonly string[]).includes(file.type)) {
    return {
      type: 'format',
      message: 'Only image (JPG, PNG, WebP, GIF) and video (MP4) files are supported',
      file,
    };
  }

  // Check size — use video-specific limit for video files
  const maxSize = isVideoFile(file)
    ? MEDIA_CONFIG.VIDEO_MAX_FILE_SIZE
    : IMAGE_CONFIG.MAX_FILE_SIZE;

  if (file.size > maxSize) {
    const sizeMB = (maxSize / (1024 * 1024)).toFixed(0);
    const fileType = isVideoFile(file) ? 'Video' : 'Image';
    return {
      type: 'size',
      message: `${fileType} size must not exceed ${sizeMB}MB`,
      file,
    };
  }

  return null;
}

/**
 * Validate multiple images
 */
export function validateImages(
  files: File[],
  existingCount: number = 0
): { valid: File[]; errors: ImageValidationError[] } {
  const valid: File[] = [];
  const errors: ImageValidationError[] = [];

  // Check total count
  if (existingCount + files.length > IMAGE_CONFIG.MAX_IMAGES) {
    errors.push({
      type: 'count',
      message: `Maximum ${IMAGE_CONFIG.MAX_IMAGES} images allowed`,
    });
    return { valid: [], errors };
  }

  // Validate each file
  files.forEach((file) => {
    const error = validateImageFile(file);
    if (error) {
      errors.push(error);
    } else {
      valid.push(file);
    }
  });

  return { valid, errors };
}

/**
 * Create object URL for preview
 */
export function createPreviewUrl(file: File): string {
  return URL.createObjectURL(file);
}

/**
 * Revoke object URL to free memory
 */
export function revokePreviewUrl(url: string): void {
  URL.revokeObjectURL(url);
}

/**
 * Format file size to human-readable string
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
}

/**
 * Load image to get dimensions
 */
export function loadImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = createPreviewUrl(file);
    
    img.onload = () => {
      revokePreviewUrl(url);
      resolve(img);
    };
    
    img.onerror = () => {
      revokePreviewUrl(url);
      reject(new Error('Failed to load image'));
    };
    
    img.src = url;
  });
}

/**
 * Resize image using canvas
 */
export function resizeImage(
  img: HTMLImageElement,
  maxWidth: number = IMAGE_CONFIG.MAX_WIDTH,
  maxHeight: number = IMAGE_CONFIG.MAX_HEIGHT
): HTMLCanvasElement {
  let width = img.width;
  let height = img.height;

  // Calculate new dimensions maintaining aspect ratio
  if (width > maxWidth || height > maxHeight) {
    const ratio = Math.min(maxWidth / width, maxHeight / height);
    width = width * ratio;
    height = height * ratio;
  }

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;

  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('Failed to get canvas context');
  }

  ctx.drawImage(img, 0, 0, width, height);
  return canvas;
}

/**
 * Create thumbnail
 */
export function createThumbnail(
  img: HTMLImageElement,
  size: number = IMAGE_CONFIG.THUMBNAIL_SIZE
): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;

  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('Failed to get canvas context');
  }

  // Calculate crop to fill square
  const sourceSize = Math.min(img.width, img.height);
  const sourceX = (img.width - sourceSize) / 2;
  const sourceY = (img.height - sourceSize) / 2;

  ctx.drawImage(
    img,
    sourceX,
    sourceY,
    sourceSize,
    sourceSize,
    0,
    0,
    size,
    size
  );

  return canvas;
}

/**
 * Convert canvas to blob
 */
export function canvasToBlob(
  canvas: HTMLCanvasElement,
  type: string = 'image/jpeg',
  quality: number = IMAGE_CONFIG.COMPRESSION_QUALITY
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error('Failed to convert canvas to blob'));
        }
      },
      type,
      quality
    );
  });
}

/**
 * Optimize image (resize + compress)
 */
export async function optimizeImage(file: File): Promise<File> {
  try {
    // Skip optimization for GIFs (preserve animation) and videos (not image data)
    if (file.type === 'image/gif' || isVideoFile(file)) {
      return file;
    }

    // Load original image
    const img = await loadImage(file);

    // Check if optimization is needed
    const needsResize = img.width > IMAGE_CONFIG.MAX_WIDTH || img.height > IMAGE_CONFIG.MAX_HEIGHT;
    
    if (!needsResize && file.size < 2 * 1024 * 1024) {
      // Image is already small enough, return original
      return file;
    }

    // Resize if needed
    const canvas = resizeImage(img);

    // Preserve original MIME type for compression (fallback to JPEG)
    const outputMimeMap: Record<string, string> = {
      'image/png': 'image/png',
      'image/webp': 'image/webp',
      'image/jpeg': 'image/jpeg',
      'image/jpg': 'image/jpeg',
    };
    const outputType = outputMimeMap[file.type] || 'image/jpeg';
    
    const blob = await canvasToBlob(canvas, outputType);

    // Create new file preserving original name and extension
    const optimizedFile = new File([blob], file.name, {
      type: outputType,
      lastModified: Date.now(),
    });

    return optimizedFile;
  } catch (error) {
    console.error('Image optimization failed:', error);
    // Return original file if optimization fails
    return file;
  }
}

/**
 * Generate thumbnail URL from file
 */
export async function generateThumbnail(file: File): Promise<string> {
  try {
    const img = await loadImage(file);
    const canvas = createThumbnail(img);
    const blob = await canvasToBlob(canvas, 'image/jpeg', 0.8);
    return URL.createObjectURL(blob);
  } catch (error) {
    console.error('Thumbnail generation failed:', error);
    // Return original preview as fallback
    return createPreviewUrl(file);
  }
}

/**
 * Generate thumbnail from a video file by capturing a frame at ~1s
 */
export async function generateVideoThumbnail(file: File): Promise<string> {
  return new Promise((resolve) => {
    const video = document.createElement('video');
    const url = createPreviewUrl(file);

    video.muted = true;
    video.playsInline = true;
    video.preload = 'auto';

    const cleanup = () => {
      video.removeAttribute('src');
      video.load();
    };

    video.addEventListener('loadeddata', () => {
      // Seek to 1s (or 0 for very short videos)
      video.currentTime = Math.min(1, video.duration / 2);
    });

    video.addEventListener('seeked', () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = IMAGE_CONFIG.THUMBNAIL_SIZE;
        canvas.height = IMAGE_CONFIG.THUMBNAIL_SIZE;

        const ctx = canvas.getContext('2d');
        if (!ctx) {
          cleanup();
          resolve(url); // fallback to blob URL
          return;
        }

        // Draw video frame cropped to square
        const sourceSize = Math.min(video.videoWidth, video.videoHeight);
        const sourceX = (video.videoWidth - sourceSize) / 2;
        const sourceY = (video.videoHeight - sourceSize) / 2;

        ctx.drawImage(
          video,
          sourceX, sourceY, sourceSize, sourceSize,
          0, 0, IMAGE_CONFIG.THUMBNAIL_SIZE, IMAGE_CONFIG.THUMBNAIL_SIZE
        );

        canvas.toBlob(
          (blob) => {
            cleanup();
            if (blob) {
              resolve(URL.createObjectURL(blob));
            } else {
              resolve(url);
            }
          },
          'image/jpeg',
          0.8
        );
      } catch {
        cleanup();
        resolve(url);
      }
    });

    video.addEventListener('error', () => {
      cleanup();
      resolve(url); // fallback to blob URL
    });

    video.src = url;
  });
}

/**
 * Extract filename without extension
 */
export function getFileNameWithoutExtension(filename: string): string {
  return filename.replace(/\.[^/.]+$/, '');
}

/**
 * Generate unique ID for uploaded image
 */
export function generateImageId(): string {
  return `img_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Check if a URL is a YouTube video link
 */
export function isYouTubeUrl(url: string): boolean {
  return /^https?:\/\/(www\.)?(youtube\.com\/watch|youtu\.be\/)/.test(url);
}

/**
 * Extract YouTube video ID from a YouTube URL
 * Supports youtube.com/watch?v=ID and youtu.be/ID formats
 */
export function extractYouTubeId(url: string): string | null {
  const watchMatch = url.match(/[?&]v=([a-zA-Z0-9_-]{11})/);
  if (watchMatch) return watchMatch[1] ?? null;
  const shortMatch = url.match(/youtu\.be\/([a-zA-Z0-9_-]{11})/);
  return shortMatch ? (shortMatch[1] ?? null) : null;
}

/**
 * Check if a URL is an embeddable Instagram reel/post/tv link.
 * Requires a /reel|reels|p|tv/{shortcode} path so plain profile URLs aren't matched.
 */
export function isInstagramUrl(url: string): boolean {
  if (!url) return false;
  return /^https?:\/\/(?:www\.)?instagram\.com\/(reels?|p|tv)\/[A-Za-z0-9_-]+/i.test(url);
}

/** True if the URL is a supported video/embed link (YouTube or Instagram). */
export function isVideoLinkUrl(url: string): boolean {
  return isYouTubeUrl(url) || isInstagramUrl(url);
}
